import {useEffect, useState, Dispatch} from "react";
import LinkifyIt from "linkify-it";
import {MatchInfo} from "react-linkify";
import {ApolloQueryResult, useApolloClient} from "@apollo/client";
import blogPostDetailQuery from "shared/Blog/queries/blogPostDetailQuery";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";
import MentorProfileQuery from "shared/Mentor/queries/MentorProfileQuery";
import get from "lodash/get";

const linkify = LinkifyIt();

type MetaData = {title: string; description: string; image: string; url: string};

const isUnibuddyOgLink = (link: MatchInfo): boolean => {
    return link?.schema === "https:" && link?.text?.includes("og");
};

const getMetaProperty = (head: HTMLHeadElement, property: string): string => {
    const meta: HTMLMetaElement = head.querySelector(`meta[property='og:${property}']`);
    return meta.content;
};

const getMetaData = (htmlString: string): MetaData => {
    const parser = new DOMParser();
    const parsed: Document = parser.parseFromString(htmlString, "text/html");
    const title = getMetaProperty(parsed.head, "title");
    const description = getMetaProperty(parsed.head, "description");
    const image = getMetaProperty(parsed.head, "image");
    const url = getMetaProperty(parsed.head, "url");
    return {title, description, image, url};
};

const getSocialMediaDataFromLink = async (link: string): Promise<MetaData> => {
    return new Promise((resolve, reject) => {
        fetch(`${link}`)
            .then((res) => {
                return res?.text();
            })
            .then((data) => {
                if (data) {
                    const metaData = getMetaData(data);
                    return resolve(metaData);
                }
            })
            .catch(() => reject({}));
    });
};

const getOpenGraphMetadata = (
    elements: Array<MatchInfo>,
    setLoadingCard: Dispatch<boolean>,
): Array<Promise<MetaData>> => {
    return elements
        .filter((el) => {
            return isUnibuddyOgLink(el);
        })
        .map((el: MatchInfo) => {
            setLoadingCard(true);
            const matchedUrl: string = get(el, "url");
            return getSocialMediaDataFromLink(matchedUrl);
        });
};

type ParsedMessages = Array<object>;

export const useParseMessages = ({
    text,
    isDegreeTypesEnabled,
    isBlogsEnabled,
    setLoadingCard,
}: {
    text: string;
    isDegreeTypesEnabled: boolean;
    isBlogsEnabled: boolean;
    setLoadingCard: Dispatch<boolean>;
}): ParsedMessages => {
    const [messages, setMessages] = useState<ParsedMessages>([]);
    const client = useApolloClient();
    const {reportError} = useErrorReporting();

    useEffect(() => {
        const Links: MatchInfo[] = linkify.match(text || "") || [];
        const openGraphMetadata = getOpenGraphMetadata(Links, setLoadingCard);
        Promise.all(openGraphMetadata)
            .then((result) => {
                return Promise.allSettled(
                    result.map((metadata): Promise<ApolloQueryResult<any>> | string => {
                        if (metadata?.url?.includes("buddies")) {
                            const buddyId = metadata.url.split("?")[0].split("/").pop();
                            return client.query({
                                query: MentorProfileQuery,
                                variables: {
                                    id: buddyId,
                                    isDegreeTypesEnabled,
                                    isBlogsEnabled,
                                },
                            });
                        } else if (metadata?.url?.includes("blogs")) {
                            const postId = metadata.url.split("?")[0].split("/").pop();
                            return client.query({
                                query: blogPostDetailQuery,
                                variables: {postId},
                            });
                        }
                        return "";
                    }),
                );
            })
            .then((result: Array<PromiseSettledResult<ApolloQueryResult<object>>>) => {
                setLoadingCard(false);
                // Each promise from the above array will either get fulfilled or rejected.
                const fulfilledResults = result.reduce((acc: ParsedMessages, eachEl) => {
                    if (eachEl?.status === "fulfilled") {
                        const data = eachEl.value?.data;
                        acc.push(data);
                    }
                    return acc;
                }, []);
                setMessages(fulfilledResults);
            })
            .catch((e) => {
                setLoadingCard(false);
                reportError(e);
            });
    }, [client, reportError, text, isDegreeTypesEnabled, isBlogsEnabled, setLoadingCard]);

    return messages;
};
